import env from './env';

// Read Cognito config from runtime env (window.__ENV__) with Vite fallback.
// Local `npm run dev` uses .env file or hardcoded defaults below.
const cognitoDomain =
  env.get('VITE_COGNITO_DOMAIN') ||
  "bmc-dev-305137865318-auth.auth.ap-south-1.amazoncognito.com";

const clientId =
  env.get('VITE_COGNITO_FRONTEND_CLIENT_ID') ||
  "7mp489d5e948kvnkbffhskgkoe";

const authority =
  env.get('VITE_COGNITO_AUTHORITY') ||
  "https://cognito-idp.ap-south-1.amazonaws.com/ap-south-1_NqR2GhzMF";

const scope =
  env.get('VITE_COGNITO_SCOPE') ||
  "openid email infra-aws-dev-api/read";

const cognitoAuthConfig = {
  authority,
  client_id: clientId,
  redirect_uri: window.location.origin + "/auth/callback",
  response_type: "code",
  scope,

  // Enable automatic silent renew for better session management
  automaticSilentRenew: true,

  // Additional configuration for signup
  filterProtocolClaims: true,
  loadUserInfo: true,

  // IMPORTANT: Ensure the callback processes the code
  monitorSession: false,
};

export const getCognitoSignupUrl = () => {
  const redirectUri = encodeURIComponent(cognitoAuthConfig.redirect_uri);
  const encodedScope = encodeURIComponent(cognitoAuthConfig.scope);
  return `https://${cognitoDomain}/signup?client_id=${clientId}&response_type=code&scope=${encodedScope}&redirect_uri=${redirectUri}`;
};

export const cognitoLogout = () => {
  // ── 1. App-managed session keys ──────────────────────────────────────────
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('bmc_active_role');
  sessionStorage.removeItem('role_switch_welcome');
  sessionStorage.removeItem('bmc:search-context');
  localStorage.removeItem('role');
  localStorage.removeItem('flow');
  localStorage.removeItem('bmc:anonymous-search-context');
  localStorage.removeItem('bmc:pending-intent');

  // Clear all bmc:mock:* keys (mock data store)
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('bmc:mock:')) localStorage.removeItem(key);
  });

  // ── 2. Amplify v6 stored tokens ──────────────────────────────────────────
  // Amplify stores tokens in localStorage under keys that start with
  // "CognitoIdentityServiceProvider.{clientId}" and "amplify-*".
  // We clear them manually so no stale tokens remain after the page reload
  // (bypassing signOut() avoids the brief Cognito login-page flash).
  const amplifyPrefixes = [
    `CognitoIdentityServiceProvider.${clientId}`,
    'amplify-signin-with-hostedui',
    'amplify.',
  ];
  [...Object.keys(localStorage), ...Object.keys(sessionStorage)].forEach(key => {
    if (amplifyPrefixes.some(prefix => key.startsWith(prefix))) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  });

  // ── 3. Non-HttpOnly cookies on the app domain ────────────────────────────
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const parentDomain = parts.length > 1 ? parts.slice(-2).join('.') : hostname;
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  const paths = ['/', '/api'];
  document.cookie.split(";").forEach(cookie => {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
    if (!name) return;
    paths.forEach(path => {
      document.cookie = `${name}=;${expire};path=${path}`;
      document.cookie = `${name}=;${expire};path=${path};domain=${hostname}`;
      document.cookie = `${name}=;${expire};path=${path};domain=.${hostname}`;
      if (parentDomain !== hostname) {
        document.cookie = `${name}=;${expire};path=${path};domain=.${parentDomain}`;
      }
    });
  });

  // ── 4. Navigate to Cognito /logout ───────────────────────────────────────
  // Cognito's hosted-UI /logout endpoint:
  //  • Invalidates the server-side session (refresh token revocation)
  //  • Clears the HttpOnly Cognito session cookies on the auth.* domain
  //  • Redirects back to our root via logout_uri
  //
  // We go here directly instead of calling signOut({ global: true }) because
  // Amplify's OAuth signOut briefly renders the Cognito login page before the
  // logout_uri redirect completes, causing a visible flash.
  const logoutUri = encodeURIComponent(window.location.origin + "/");
  window.location.href = `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
};

export const cognitoTokenEndpoint = `https://${cognitoDomain}/oauth2/token`;

export { cognitoAuthConfig, clientId };
